import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { plain } from "@/lib/plain";
import { cn } from "@/lib/util";
import { KeyRound, RefreshCw, Loader2, ShieldAlert, AlertTriangle, ExternalLink } from "lucide-react";

const SEV_TONE = {
  critical: "border-sev-critical/60 text-sev-critical bg-sev-critical/10",
  high: "border-sev-high/60 text-sev-high bg-sev-high/10",
  medium: "border-sev-medium/60 text-sev-medium bg-sev-medium/10",
  low: "border-sev-low/60 text-sev-low bg-sev-low/10",
};

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleDateString();
  } catch { return String(v); }
};

export function UnifiedLeaksSection({ clientId, clients, onClientChange }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/leaks/unified/${clientId}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar filtraciones");
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/leaks/status").then(r => setStatus(r.data)).catch(() => {}); }, []);

  const records = data?.records || [];
  const totals = data?.totals || { records: 0, breaches: 0, exposed_addresses: 0 };
  const sources = data?.sources || {};

  const groupedBySource = useMemo(() => {
    return records.reduce((acc, r) => { (acc[r.source] ||= []).push(r); return acc; }, {});
  }, [records]);

  return (
    <section className="border border-border bg-card/60" data-testid="unified-leaks-section">
      <div className="p-5 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-sev-high" />
          <h2 className="font-display text-lg font-medium">Filtraciones de datos (vista unificada)</h2>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground ml-2">
            HIBP + IntelX
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select value={clientId || ""} onChange={e => onClientChange?.(e.target.value)}
                  className="bg-background border border-border px-3 py-1.5 text-xs font-mono focus:border-primary outline-none"
                  data-testid="leaks-client-selector">
            {clients.map(c => <option key={c.id} value={c.id}>{plain(c.name, `#${c.id.slice(0,8)}`)}</option>)}
          </select>
          <button onClick={load} disabled={loading}
                  className="border border-border p-1.5 hover:border-primary hover:text-primary disabled:opacity-30"
                  data-testid="leaks-reload-btn">
            <RefreshCw className={cn("w-3 h-3", loading && "animate-spin")} />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <TotalChip label="Filtraciones" value={totals.records} />
          <TotalChip label="Brechas únicas" value={totals.breaches} />
          <TotalChip label="Direcciones expuestas" value={totals.exposed_addresses} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs font-mono">
          <SourceBadge name="HIBP" configured={status?.hibp_configured}
                        skipped={sources.hibp?.skipped}
                        detail={
                          sources.hibp?.skipped ? sources.hibp.reason :
                          `${Object.keys(sources.hibp?.domains || {}).length} dominios consultados`
                        } />
          <SourceBadge name="IntelX" configured={status?.intelx_configured}
                        skipped={sources.intelx?.skipped}
                        detail={
                          sources.intelx?.skipped ? sources.intelx.reason :
                          `${sources.intelx?.count || 0} exposiciones · último escaneo ${fmtDate(sources.intelx?.last_scan_at)}`
                        } />
          <SourceBadge name="DeHashed" configured={false} skipped
                        detail="Planificado — omitido en esta iteración" />
        </div>

        {data?.errors?.length > 0 && (
          <details className="border border-sev-medium/40 bg-sev-medium/5 p-3 text-xs">
            <summary className="cursor-pointer text-sev-medium font-mono uppercase tracking-widest text-[10px] inline-flex items-center gap-2">
              <AlertTriangle className="w-3 h-3" />
              {data.errors.length} advertencia{data.errors.length === 1 ? "" : "s"}
            </summary>
            <ul className="mt-2 space-y-1 font-mono text-[11px] text-muted-foreground">
              {data.errors.map((e, i) => <li key={i}>[{e.source}] {e.message}</li>)}
            </ul>
          </details>
        )}

        {records.length === 0 ? (
          <div className="border border-border bg-background/40 p-8 text-center text-xs font-mono text-muted-foreground">
            {(!status?.hibp_configured && !status?.intelx_configured)
              ? "Configura HIBP_API_KEY y/o INTELX_API_KEY para poblar esta vista."
              : "Sin filtraciones detectadas en las fuentes activas."}
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedBySource).map(([src, rows]) => (
              <div key={src} className="border border-border bg-background/40" data-testid={`leaks-source-${src}`}>
                <div className="px-4 py-2 border-b border-border flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-primary">{src}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{rows.length} registros</span>
                </div>
                <div className="divide-y divide-border/40 max-h-[360px] overflow-y-auto">
                  {rows.map((r, i) => <LeakRow key={`${src}-${i}`} r={r} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function TotalChip({ label, value }) {
  return (
    <div className="border border-border p-3">
      <div className="overline">{label}</div>
      <div className="font-display text-2xl mt-1">{value || 0}</div>
    </div>
  );
}

function SourceBadge({ name, configured, skipped, detail }) {
  const tone = configured && !skipped ? "border-sev-low/40 text-sev-low"
               : skipped ? "border-muted text-muted-foreground"
               : "border-sev-high/40 text-sev-high";
  return (
    <div className={cn("border p-3", tone)}>
      <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest">
        {name} · {configured && !skipped ? "activa" : skipped ? "omitida" : "no configurada"}
      </div>
      <div className="text-[10px] mt-1 opacity-80">{detail}</div>
    </div>
  );
}

function LeakRow({ r }) {
  return (
    <div className="px-4 py-3 hover:bg-muted/20" data-testid="leaks-row">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert className="w-3 h-3 text-primary shrink-0" />
          <span className="text-sm truncate font-medium">{r.breach_title || r.breach || "—"}</span>
        </div>
        <span className={cn("border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest shrink-0",
                            SEV_TONE[r.severity] || SEV_TONE.medium)}>
          {r.severity}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-muted-foreground">
        <span className="uppercase tracking-widest">{r.type}</span>
        <span>·</span>
        <span>{fmtDate(r.date)}</span>
        {r.value && <><span>·</span><span className="text-foreground/80">{r.value}</span></>}
        {r.addresses_exposed ? <><span>·</span><span>{r.addresses_exposed} direcciones</span></> : null}
        {r.pwn_count ? <><span>·</span><span>{Number(r.pwn_count).toLocaleString()} pwn</span></> : null}
      </div>
      {Array.isArray(r.exposed_data) && r.exposed_data.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {r.exposed_data.slice(0, 12).map((d, i) => (
            <span key={i} className="border border-border/60 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
              {d}
            </span>
          ))}
        </div>
      )}
      {r.description && (
        <p className="mt-2 text-[11px] text-muted-foreground line-clamp-2">{r.description}</p>
      )}
      {Array.isArray(r.sample_aliases) && r.sample_aliases.length > 0 && (
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">
          alias muestreados: {r.sample_aliases.slice(0, 5).join(", ")}
        </div>
      )}
    </div>
  );
}
